import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';

import DataGridPager from '~components/DataGrid/DataGridPager';
import DataTable from '~components/DataTable/DataTable';
import { DEFAULT_API_PAGE_SIZE } from '~constants/consts';
import { Timesheet } from '~hooks/useTimesheets/models';
import useTimesheets from '~hooks/useTimesheets/useTimesheets';
import TimesheetDrawer from '~pages/Approvals/shared/TimesheetDrawer/TimesheetDrawer';
import useCursorPaginationQuery from '~queries/useCursorPaginationQuery';
import { PaginationLink } from '~services/pagination';
import { useStores } from '~store/RootStore';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import {
  TIMESHEET_COLUMNS,
  transformTimesheetToRow,
} from './timesheetsColumnDefinitions';
import TimesheetUploadButton from './TimesheetUploadButton';

// AI vision extraction runs asynchronously on the backend with no realtime channel, so
// after an upload we poll the just-created timesheets by id until each finishes scanning.
// This targets only the rows we're waiting on instead of reloading the whole page.
const SCAN_POLL_INTERVAL_MS = 5000; // check scan status every 5s
const MAX_SCAN_POLL_ATTEMPTS = 12; // give up after ~1 min so a stuck extraction never polls forever

export const Timesheets = observer(() => {
  const { userStore } = useStores();
  const { getTimesheetsPage, getTimesheetById } = useTimesheets();
  const [pendingScanIds, setPendingScanIds] = useState<string[]>([]);
  const [selected, setSelected] = useState<{
    isDrawerOpen: boolean;
    timesheet: Timesheet | null;
  }>({ isDrawerOpen: false, timesheet: null });

  const companyId = getEffectiveUserCompanyId(userStore);

  const {
    goToPage,
    isFetching,
    pagination,
    reloadCurrentPage,
    rows: timesheets,
    startOver,
  } = useCursorPaginationQuery<Timesheet>({
    fetchPage: ({ cursor, limit, link, signal }) => {
      if (!companyId) {
        return Promise.resolve({ data: [], pagination: { limit, page: 1 } });
      }

      return getTimesheetsPage(companyId, { cursor, limit, link, signal });
    },
    limit: DEFAULT_API_PAGE_SIZE,
    queryKey: ['timesheets', companyId],
  });

  useEffect(() => {
    setPendingScanIds([]);
    setSelected({ isDrawerOpen: false, timesheet: null });
  }, [companyId]);

  useEffect(() => {
    const openTimesheetId = selected.timesheet?.id;
    if (!openTimesheetId) {
      return;
    }

    const updated = timesheets.find((timesheet) => timesheet.id === openTimesheetId);
    if (updated && updated !== selected.timesheet) {
      setSelected((state) => ({ ...state, timesheet: updated }));
    }
  }, [timesheets, selected.timesheet]);

  const handlePageChange = useCallback(
    (link: PaginationLink) => {
      void goToPage(link);
    },
    [goToPage],
  );

  // A reload would abort a page navigation in flight and reload the page being left, and the
  // navigation loads fresh rows anyway
  const isFetchingRef = useRef(isFetching);
  isFetchingRef.current = isFetching;

  const handleChanged = useCallback(() => {
    if (isFetchingRef.current) {
      return;
    }

    void reloadCurrentPage();
  }, [reloadCurrentPage]);

  // Track the just-uploaded timesheets that are still processing so the poll below knows
  // exactly which rows to watch.
  const handleUploaded = useCallback(
    (uploadedTimesheets: Timesheet[]) => {
      const stillProcessingIds = uploadedTimesheets
        .filter((timesheet) => !timesheet.aiProcessed)
        .map((timesheet) => timesheet.id);

      if (stillProcessingIds.length > 0) {
        setPendingScanIds((previous) => [
          ...new Set([...previous, ...stillProcessingIds]),
        ]);
      }

      void startOver();
    },
    [startOver],
  );

  useEffect(() => {
    if (!companyId || pendingScanIds.length === 0) {
      return;
    }

    let attempts = 0;
    const intervalId = setInterval(async () => {
      attempts += 1;
      if (attempts > MAX_SCAN_POLL_ATTEMPTS) {
        clearInterval(intervalId);

        return;
      }

      // Check each pending timesheet on its own; a failed check just retries next tick.
      // Use silent mode to avoid spamming toasts on transient network errors.
      const polled = await Promise.all(
        pendingScanIds.map((id) => getTimesheetById(companyId, id, { silent: true })),
      );

      const scannedIds = polled.flatMap((timesheet) =>
        timesheet?.aiProcessed ? [timesheet.id] : [],
      );

      if (scannedIds.length > 0) {
        setPendingScanIds((previous) =>
          previous.filter((id) => !scannedIds.includes(id)),
        );
        handleChanged();
      }
    }, SCAN_POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [companyId, pendingScanIds, getTimesheetById, handleChanged]);

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'flex-end', mb: 2 }}>
        <TimesheetUploadButton onUploaded={handleUploaded} />
      </Stack>
      <DataTable
        id="timesheets-grid"
        columns={TIMESHEET_COLUMNS}
        loading={isFetching}
        onRowClick={(row) => {
          const clicked = timesheets.find((timesheet) => timesheet.id === row.id);
          setSelected({ isDrawerOpen: true, timesheet: clicked ?? null });
        }}
        rows={timesheets.map(transformTimesheetToRow)}
        headerTrailingComponent={
          <DataGridPager
            isFetching={isFetching}
            onNavigate={handlePageChange}
            pagination={pagination}
          />
        }
      />
      <TimesheetDrawer
        companyId={companyId}
        onClose={() => setSelected((state) => ({ ...state, isDrawerOpen: false }))}
        onChanged={handleChanged}
        onScanned={handleChanged}
        open={selected.isDrawerOpen}
        timesheet={selected.timesheet}
      />
    </Box>
  );
});
